import EquipmentAndSitesSection from "components/dashboard/EquipmentAndSitesSection";
import SnapshotSection from "components/dashboard/SnapshotSection";
import SpendSummary from "components/dashboard/SpendSummary";
import Container from "components/general/Container";
import Seo from "components/general/Seo";
import { Box } from "components/ui";
import { Typography } from "components/ui/Typography";
import { ArrowUpFromLine, BadgeInfo, Ban, Book, CircleAlert, Package2, Settings, ShoppingCart, TagIcon, TrendingUp, Truck } from "lucide-react-native";
import React from "react";
import { useGetDashboardEquipmentsQuery, useGetDashboardOrdersQuery, useGetDashboardProductsQuery, useGetDashboardSitesQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import SiteListSection from "./SiteListSection";

export default function DashboardAdmin() {

	const oneYearAgo = new Date();
	oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
	oneYearAgo.setHours(0, 0, 0, 0);

	const { data: orders, isLoading: ordersIsLoading, isFetching: ordersIsFetching } = useGetDashboardOrdersQuery({});
	const { data: products, isLoading: productsIsLoading, isFetching: productsIsFetching } = useGetDashboardProductsQuery({});
	const { data: sites, isLoading: siteIsLoading, isFetching: siteIsFetching } = useGetDashboardSitesQuery({});
	const { data: equipments, isLoading: equipmentsIsLoading, isFetching: equipmentsIsFetching } = useGetDashboardEquipmentsQuery({});
	const productPanelList = [
		{
			description: "different products ordered",
			value: products?.uniqueItemsOrdered ?? 0,
			icon: <TagIcon />,
		},
		{
			hasAlert: (products?.catalogueDiscontinuedItems ?? 0) > 0,
			description: "catalogues with discontinued products",
			value: products?.catalogueDiscontinuedItems ?? 0,
			icon: <Book />,
		},
		{
			hasAlert: (products?.catalogueSpecialItems ?? 0) > 0,
			description: "catalogues with special order products",
			value: products?.catalogueSpecialItems ?? 0,
			icon: <Book />,
		},
		{
			hasAlert: (products?.stocklistDiscontinuedItems ?? 0) > 0,
			description: "stocklist with discontinued products",
			value: products?.stocklistDiscontinuedItems ?? 0,
			icon: <Ban />,
		},
	];

	const orderPanelList = [
		{
			description: "orders placed",
			value: orders?.currentYear?.orders ?? 0,
			icon: <ShoppingCart />,
			link: "/orders",
			params: { 
				startDateQuery: oneYearAgo.toUTCString(),
				orderTypeQuery: 'salesorder' 
			}
		},
		{
			hasAlert: (orders?.currentYear?.awaitingApproval ?? 0) > 0,
			description: "orders awaiting approval",
			value: orders?.currentYear?.awaitingApproval ?? 0,
			icon: <Package2 />,
			link: "/orders",
			params: { 
				statusQuery: 'Awaiting Approval',
			}
		},
		{
			hasAlert: (orders?.currentYear?.backOrderedItems ?? 0) > 0,
			description: "orders with backorder",
			value: orders?.currentYear?.backOrderedItems ?? 0,
			icon: <ShoppingCart />,
		},
	];

	const equipmentPanelList = [
		{
			hasAlert: false,
			description: "machines serviced with Abco",
			value: equipments?.servicedByAbco ?? 0,
			icon: <Settings />,
			link: "/equipment/my-equipment",
		},
		{
			hasAlert: (equipments?.dueForService ?? 0) > 0,
			description: "machines overdue for service",
			value: equipments?.dueForService ?? 0,
			icon: <CircleAlert />,
			link: "/equipment/service-orders",
		},
		// {
		// 	hasAlert: false,
		// 	description: "continuous machine hrs of operation",
		// 	value: equipments?.totalMachineHours,
		// 	icon: <Clock />,
		// },
		// {
		// 	hasAlert: (equipments?.hireDueForReturn ?? 0) > 0,
		// 	description: "hire machines due for return",
		// 	value: equipments?.hireDueForReturn,
		// 	icon: <Undo2 />,
		// },
		{
			hasAlert: (equipments?.outstandingEmergencyRepairs ?? 0) > 0,
			description: "outstanding emergency repairs",
			value: equipments?.outstandingEmergencyRepairs ?? 0,
			icon: <BadgeInfo />,
			link: "/equipment/service-orders",
		},
	];

	const sitesAndGroupsPanelList = [
		{
			hasAlert: (sites?.overBudget ?? 0) > 0,
			description: "sites over budget",
			value: sites?.overBudget ?? 0,
			icon: <ArrowUpFromLine />,
		},
		{
			hasAlert: (sites?.overBudgetGroups ?? 0) > 0,
			description: "groups over budget",
			value: sites?.overBudgetGroups ?? 0,
			icon: <TrendingUp />,
		},
		{
			hasAlert: false,
			description: "sites with no order placed in last 90 days",
			value: sites?.withoutActivity ?? 0,
			icon: <ShoppingCart />,
		},
		{
			hasAlert: false,
			description: "sites with no budget",
			value: sites?.withoutBudget ?? 0,
			icon: <Truck />,
		},
		// {
		// 	hasAlert: false,
		// 	description: "Ungrouped sites",
		// 	value: sites?.withoutGroup,
		// 	icon: <Scan />,
		// },
	];

	const isMobile = useIsMobile();
	return (
		<Box>
			<Seo title="Dashboard | Abconet" description="Get a comprehensive overview of system activity, performance metrics, and key insights." />
			<Container>
				<Box flexDirection="row" alignItems="center" px={16} mt={24} my={isMobile ? 12 : 24}>
					<Typography.H1 weight="bold">My Dashboard</Typography.H1>
					{/* <Box ml={"auto"}>
						<Button py={isMobile ? 16 : 20} px={isMobile ? 16 : "auto"} borderRadius={16} gap={6} backgroundColor="$primarylight" variant="primary">
							<ListTree color={"white"} />
							{!isMobile && <Text color="$basicwhite">Customize</Text>}
						</Button>
					</Box> */}
				</Box>

				<Box borderRadius={12} bg="$neutral35" my={16}>
					<Box p={24}>
						<SiteListSection />
					</Box>
				</Box>

				<Box borderRadius={12} bg="$neutral35" my={16}>
					<Box>
						<SpendSummary />
					</Box>
					{/* <Box width={"$full"} bg="$basicwhite" h={4} />
					<Box>
						<SustainabilitySection />
					</Box> */}
				</Box>
			</Container>
			<Box bg="$neutral35">
				<Container>
					<SnapshotSection orderPanelList={orderPanelList} ordersIsLoading={ordersIsLoading} productPanelList={productPanelList} products={products} productsIsLoading={productsIsLoading} sites={sites} />
					<EquipmentAndSitesSection equipmentPanelList={equipmentPanelList} equipmentsIsLoading={equipmentsIsLoading} sitesAndGroupsPanelList={sitesAndGroupsPanelList} siteIsLoading={siteIsLoading} sites={sites} />
				</Container>
			</Box>
		</Box>
	);
}
